// The Trello card on an Active Builds page: view it, pick one from the client's
// board, change it or remove it. Self-sufficient like CommsEventChip: it loads
// the client's board and its open cards itself. The write goes back through
// onSave so pages/ActiveBuilds.tsx owns it.

import { useEffect, useMemo, useState } from 'react';
import { Button, SearchableSelect, SidePanel } from '../ui';
import TrelloCardDetailBody from '../comms/TrelloCardDetailBody';
import { fetchClientBoard, type BuildPage, type PageCard } from '../../lib/activeBuilds';
import { invokeTrelloBoard, type TrelloCard, type TrelloList } from '../../lib/trelloBoard';

interface PageCardPanelProps {
  page: BuildPage | null;
  clientId: string | null;
  busy: boolean;
  onClose: () => void;
  onSave: (page: BuildPage, card: PageCard | null) => void;
}

export default function PageCardPanel({ page, clientId, busy, onClose, onSave }: PageCardPanelProps) {
  const [board, setBoard] = useState<{ id: string; trello_board_id: string; board_name: string } | null>(null);
  const [lists, setLists] = useState<TrelloList[]>([]);
  const [cards, setCards] = useState<TrelloCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const open = !!page;

  useEffect(() => {
    setChoice(page?.card_id ?? null);
  }, [page?.id, page?.card_id]);

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBoard(null);
    setLists([]);
    setCards([]);
    (async () => {
      try {
        const row = await fetchClientBoard(clientId);
        if (cancelled) return;
        setBoard(row);
        if (!row) return;
        const data = await invokeTrelloBoard<{ lists: TrelloList[]; cards: TrelloCard[] }>({
          action: 'board',
          boardId: row.trello_board_id,
        });
        if (cancelled) return;
        setLists(data.lists || []);
        setCards(data.cards || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading the client Trello board:', err);
        setError('Failed to load the Trello board');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  const listName = useMemo(() => new Map(lists.map((l) => [l.id, l.name])), [lists]);
  const listPos = useMemo(() => new Map(lists.map((l) => [l.id, l.pos])), [lists]);

  // Board order: list by list, cards in their list's order
  const options = useMemo(
    () =>
      cards
        .slice()
        .sort((a, b) => (listPos.get(a.idList) ?? 0) - (listPos.get(b.idList) ?? 0) || a.pos - b.pos)
        .map((c) => ({ value: c.id, label: c.name, hint: listName.get(c.idList) ?? undefined })),
    [cards, listName, listPos],
  );

  const picked = cards.find((c) => c.id === choice) ?? null;
  const changed = (choice ?? null) !== (page?.card_id ?? null);
  const onBoard = page?.status === 'queued';

  const save = () => {
    if (!page) return;
    if (!choice) {
      onSave(page, null);
      return;
    }
    if (!picked || !board) return;
    onSave(page, { id: picked.id, name: picked.name, url: picked.shortUrl, listName: listName.get(picked.idList) ?? null, boardId: board.id });
  };

  return (
    <SidePanel
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      eyebrow="Trello card"
      title={page?.title ?? ''}
      width={560}
      footer={
        <>
          <Button onClick={save} disabled={busy || !changed || (!!choice && !picked)}>
            {choice ? (page?.card_id ? 'Change card' : 'Attach card') : 'Remove card'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </>
      }
    >
      <p style={{ margin: '0 0 14px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
        Optional. When the page goes to the Fulfillment board, its task gets this card as a comms chip, so whoever builds it reads the card there.
        {onBoard ? ' This page is already on the board, so attaching links the card to its task now.' : ''}
      </p>

      {loading ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading the board…</div>
      ) : error ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</div>
      ) : !board ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          This client has no Trello board in Client Comms. Assign one on the client’s Trello panel to attach cards.
        </div>
      ) : (
        <SearchableSelect
          label={`Card on ${board.board_name}`}
          placeholder="Pick a card…"
          searchPlaceholder="Search cards…"
          clearLabel="No card"
          allowClear
          options={options}
          value={choice}
          onChange={setChoice}
          menuMinWidth={420}
        />
      )}

      {page?.card_id && choice === page.card_id && !picked && !loading && board ? (
        <div style={{ marginTop: 8, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {page.card_name} isn’t an open card on this board any more. It still goes with the page.
        </div>
      ) : null}

      {choice ? (
        <div style={{ marginTop: 18 }}>
          <div className="t-eyebrow" style={{ marginBottom: 8 }}>Card context</div>
          <TrelloCardDetailBody cardId={choice} />
        </div>
      ) : null}
    </SidePanel>
  );
}
